import React, { useState, useEffect } from 'react';
import { Link2, Copy, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';

const WEBHOOK_PATHS = [
  { label: 'ClickUp Webhook', path: '/api/webhooks/clickup', service: 'ClickUp Automations' },
  { label: 'GHL Invoice Paid', path: '/api/webhooks/ghl', service: 'GoHighLevel Webhooks' },
];

function getBaseUrl() {
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
}

export default function WebhookInfoButton() {
  const [open, setOpen] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const baseUrl = getBaseUrl();
  const webhooks = WEBHOOK_PATHS.map(w => ({ ...w, url: `${baseUrl}${w.path}` }));
  const currentFingerprint = webhooks.map(w => w.url).join('|');

  useEffect(() => {
    const last = localStorage.getItem('lastCopiedWebhookUrl');
    if (!last || last !== currentFingerprint) {
      setIsChanged(true);
    }
  }, [currentFingerprint]);

  const handleCopy = async (url, idx) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);

      // Only clear the alert when ALL urls are acknowledged
      localStorage.setItem('lastCopiedWebhookUrl', currentFingerprint);
      setIsChanged(false);
      toast.success('Webhook URL copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <>
      <Button
        variant={isChanged ? 'default' : 'ghost'}
        size="sm"
        onClick={() => setOpen(true)}
        className={`relative transition-colors duration-150 ${
          isChanged
            ? 'bg-red-600 hover:bg-red-700 text-white'
            : 'text-muted-foreground hover:text-foreground'
        }`}
        data-testid="webhook-info-button"
      >
        <Link2 className="h-4 w-4 mr-1" />
        <span className="hidden sm:inline text-xs">Webhooks</span>
        {isChanged && (
          <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse border-2 border-white" />
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-role="heading">
              <Link2 className="h-5 w-5 text-primary" />
              Integration Webhook URLs
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-3">
            {isChanged && (
              <div className="p-3 rounded-md bg-red-50 border border-red-200 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">Webhook URL may have changed</p>
                  <p className="text-xs text-red-600 mt-0.5">
                    If this URL changed, you must update it in your ClickUp and GHL settings or automations will break.
                  </p>
                </div>
              </div>
            )}

            {webhooks.map((w, i) => (
              <div key={i} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{w.label}</p>
                    <p className="text-[10px] text-muted-foreground">Configure in: {w.service}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">POST</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted px-3 py-2 rounded font-mono break-all select-all">
                    {w.url}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 flex-shrink-0"
                    onClick={() => handleCopy(w.url, i)}
                  >
                    {copiedIdx === i ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>
            ))}

            <p className="text-[10px] text-muted-foreground text-center pt-1">
              Copying any URL acknowledges the current configuration and clears the alert.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
